import React, { useEffect, useState } from 'react';
import { Modal, Stack, Textarea, TextInput, Switch, Group, Button, Text, Select } from '@mantine/core';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import config from '../../config';

const SURFACES = [
  { value: 'nymeria', label: 'AI Sales Agent (what the assistant says)' },
  { value: 'headline', label: 'Cockpit headline (advisor)' },
  { value: 'judgement', label: 'Quoting judgment' },
];

/**
 * Shared "Teach from this" / author modal. Pre-fills from a real agent output (context +
 * the agent's actual answer, which the leader edits to the ideal) or opens blank for
 * fresh authoring. Saves a few-shot teaching example that shapes future agent calls.
 *
 * props: opened, onClose, surface (locked when seeding from a specific surface, else
 * selectable), context, idealOutput (prefill), sourceRef, onSaved.
 */
export default function TeachModal({ opened, onClose, surface, context = '', idealOutput = '', sourceRef, onSaved }) {
  const { post } = useAuthenticatedPost();
  const [sf, setSf] = useState(surface || 'nymeria');
  const [title, setTitle] = useState('');
  const [ctx, setCtx] = useState(context);
  const [ideal, setIdeal] = useState(idealOutput);
  const [why, setWhy] = useState('');
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (opened) { setSf(surface || 'nymeria'); setCtx(context); setIdeal(idealOutput); setTitle(''); setWhy(''); setPinned(false); setErr(null); } },
    [opened, surface, context, idealOutput]);

  const save = async () => {
    if (!ctx.trim() || !ideal.trim()) { setErr('Situation and ideal response are both required.'); return; }
    setSaving(true); setErr(null);
    try {
      await post(`${config.apiBaseUrl}/api/vesper/teaching-examples`, {
        surface: sf, title: title.trim() || null, context: ctx, idealOutput: ideal,
        rationale: why.trim() || null, sourceRef: sourceRef || null, pinned,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e?.response?.status === 403 ? 'Only leadership can add teaching examples.' : (e?.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Teach the system" size="lg">
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Show the agent how you'd handle this. It's saved as a worked example and played into future
          responses (few-shot) — not fine-tuning; it takes effect on the next call and you can turn it off anytime.
        </Text>
        <Select label="Applies to" data={SURFACES} value={sf} onChange={(v) => setSf(v || 'nymeria')} disabled={!!surface} />
        <TextInput label="Short title (optional)" value={title} onChange={(e) => setTitle(e.currentTarget.value)} placeholder="e.g. at-risk FAA-145 returning" />
        <Textarea label="Situation" value={ctx} onChange={(e) => setCtx(e.currentTarget.value)} minRows={3} autosize maxRows={8} required
          description="The context / what came in. (Pre-filled from the real one when teaching from an output.)" />
        <Textarea label="Ideal response" value={ideal} onChange={(e) => setIdeal(e.currentTarget.value)} minRows={3} autosize maxRows={10} required
          description="Exactly what the agent should have said / decided." />
        <Textarea label="Why (your thinking)" value={why} onChange={(e) => setWhy(e.currentTarget.value)} minRows={2} autosize maxRows={6}
          description="The reasoning — injected too, so the agent learns how you think, not just what you say." />
        <Switch label="Pin (always inject this example)" checked={pinned} onChange={(e) => setPinned(e.currentTarget.checked)} />
        {err && <Text size="sm" c="red">{err}</Text>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button color="grape" loading={saving} onClick={save}>Save example</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
